import { Button, Group, Stack } from '@mantine/core';
import { IconFileTypeXls } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FacultyFilter } from '@/module/Nomen/Faculty/FacultyFilter';
import { isTravelReportingRole } from '@/module/Travel/travelRoles';

import { Content, NoContentPlaceholder, NumberFilter, useNumberFilter } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { FilterBox, useAppNotification } from '@feature/utils';

import { TravelAggregatesPanel } from './components/TravelAggregatesPanel';
import { TravelYearlyPanel } from './components/TravelYearlyPanel';
import { useTravelStaffSummaryExport } from './model/TravelRegistryQuery';
import { DEFAULT_TRAVEL_REGISTRY_LANG, TravelReportingParams } from './model/TravelRegistryTypes';
import { useTravelFacultyFilter } from './useTravelFacultyFilter';

export function TravelAnalytics() {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { currentRole } = useCurrentUser();
  // The backend reports on the current year when none is sent, so the input starts there instead of empty.
  const { item: registrationYear, ...yearFilterProps } = useNumberFilter(new Date().getFullYear());
  const { facultyId, facultyFilterProps } = useTravelFacultyFilter(true);
  const exportStaffSummary = useTravelStaffSummaryExport();

  const params: TravelReportingParams = {
    // A half-typed year debounces to NaN, which qs would still send.
    registrationYear: registrationYear != null && Number.isFinite(registrationYear) ? registrationYear : null,
    facultyId,
  };

  const onExport = () =>
    exportStaffSummary.mutate({ ...params, lang: DEFAULT_TRAVEL_REGISTRY_LANG }, { onError: notification.error });

  if (!isTravelReportingRole(currentRole)) {
    return <NoContentPlaceholder title={t('travel.registry.aggregates.noAccess')} />;
  }

  return (
    <Stack>
      <Group justify="end">
        <Button
          size="xs"
          variant="outline"
          leftSection={<IconFileTypeXls size={14} />}
          loading={exportStaffSummary.isPending}
          onClick={onExport}
        >
          {t('travel.registry.staffSummaryExport')}
        </Button>
      </Group>
      <FilterBox>
        <Group grow>
          <NumberFilter
            label={t('travel.registry.filters.registrationYear')}
            allowDecimal={false}
            min={2000}
            {...yearFilterProps}
          />
          {facultyFilterProps && (
            <FacultyFilter
              label={t('travel.registry.filters.faculty')}
              {...facultyFilterProps}
            />
          )}
        </Group>
      </FilterBox>
      <TravelAggregatesPanel params={params} />
      <Content
        bg="white"
        title={t('travel.registry.aggregates.yearly.title')}
      >
        <TravelYearlyPanel
          year={params.registrationYear ?? new Date().getFullYear()}
          facultyId={facultyId}
        />
      </Content>
    </Stack>
  );
}
